import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { pickText } from '@telestore/contracts';
import { formatMoney, formatPhnomPenh } from '@telestore/shared';
import { useEffect } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { Empty, ErrorBox, Money, Panel, Pill, Row, Rows, Spinner, Stat } from '../components/ui.js';
import { SetupChecklist } from '../components/setup-checklist.js';

/**
 * The merchant's home screen.
 *
 * It answers exactly one question — "what needs me right now?" — in this
 * order: orders waiting on a confirm, transfers that arrived but matched no
 * order, and stock about to run out. Revenue is context, not a call to action,
 * so it comes after.
 */
export function DashboardPage() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();

  const dashboard = useQuery({
    queryKey: ['dashboard', shopId],
    queryFn: () => api.dashboard(shopId!),
    enabled: Boolean(shopId),
    refetchInterval: 60_000,
  });

  // Live updates: a new order should appear without the merchant refreshing.
  useEffect(() => {
    if (!shopId) return;
    const stream = api.stream(shopId);
    stream.onmessage = () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', shopId] });
    };
    stream.onerror = () => stream.close();
    return () => stream.close();
  }, [shopId, queryClient]);

  if (dashboard.isPending) return <Spinner label={t('common.loading')} />;
  if (dashboard.isError) {
    return (
      <ErrorBox
        message={(dashboard.error as Error).message}
        onRetry={() => void dashboard.refetch()}
        retryLabel={t('common.retry')}
      />
    );
  }

  const { stats, currency, lowStock, unmatchedPayments, credits } = dashboard.data;

  return (
    <div className="flex flex-col gap-4">
      {/*
        Above the stats on purpose: a shop still being set up has nothing in
        those numbers, and "0 orders" is not the answer to "what needs me
        right now?". It removes itself once the required steps are done.
      */}
      <SetupChecklist />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t('admin.pendingConfirm')}
          value={stats.pendingConfirm}
          tone={stats.pendingConfirm > 0 ? 'warn' : 'neutral'}
          hint={stats.pendingConfirm > 0 ? t('merchant.confirmOrder') : undefined}
        />
        <Stat label={t('admin.awaitingPayment')} value={stats.awaitingPayment} />
        <Stat label={t('admin.ordersToday')} value={stats.ordersToday} />
        <Stat
          label={t('admin.revenue')}
          value={formatMoney({ amount: stats.revenueToday, currency })}
          tone="positive"
        />
      </div>

      {/*
        An unmatched transfer is money the shop has been paid but cannot see
        against an order. It outranks everything else on this screen.
      */}
      {unmatchedPayments.length > 0 ? (
        <Panel title={t('merchant.paymentUnmatched', { amount: '' })} padded={false}>
          <Rows>
            {unmatchedPayments.map((notification) => (
              <Row key={notification.id}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {notification.amount !== null && notification.currency ? (
                      <Money amount={notification.amount} currency={notification.currency} />
                    ) : (
                      '—'
                    )}
                  </p>
                  <p className="truncate text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {notification.payerName ?? '—'}
                    {notification.occurredAt
                      ? ` · ${formatPhnomPenh(new Date(notification.occurredAt), locale)}`
                      : ''}
                  </p>
                </div>
                <Link to="/payments" className="btn btn-quiet">
                  {t('merchant.viewOrder')}
                </Link>
              </Row>
            ))}
          </Rows>
        </Panel>
      ) : null}

      {lowStock.length > 0 ? (
        <Panel title={t('merchant.lowStockTitle')} padded={false}>
          <Rows>
            {lowStock.slice(0, 6).map((item) => (
              <Row key={item.variantId}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{pickText(item.name, locale)}</p>
                  <p className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {[item.color, item.size].filter(Boolean).join(' / ') || '—'}
                  </p>
                </div>
                <Pill tone={item.stock <= 1 ? 'danger' : 'warn'}>
                  {t('common.lowStock', { count: item.stock })}
                </Pill>
              </Row>
            ))}
          </Rows>
        </Panel>
      ) : null}

      <Panel title={t('admin.credits')}>
        <div className="flex items-center justify-between">
          <p className="text-sm">{t('admin.creditsRemaining', { count: credits.available })}</p>
          <Link to="/billing" className="btn btn-quiet">
            {t('admin.topUp')}
          </Link>
        </div>
      </Panel>

      {stats.pendingConfirm === 0 &&
      stats.ordersToday === 0 &&
      unmatchedPayments.length === 0 &&
      lowStock.length === 0 ? (
        <Empty
          title={t('common.empty')}
          hint={t('admin.addProduct')}
          action={
            <Link to="/products" className="btn btn-primary">
              {t('admin.addProduct')}
            </Link>
          }
        />
      ) : null}
    </div>
  );
}
